import React, { FunctionComponent, useContext } from 'react'

import '../styles/SearchResults.scss'
import SearchItem, {
  CalendarData,
  ContactsData,
  DropboxData,
  SlackData,
  TweetData,
} from '../utils/SearchItem'
import { alphabetical, shuffle, flatten } from '../utils/helper'
import CalendarItem from './CalendarItem'
import ContactsItem from './ContactsItem'
import DropboxItem from './DropboxItem'
import SlackItem from './SlackItem'
import TweetItem from './TweetItem'
import { useFilter, useSort, useSearchQuery } from '../App'

interface Props {
  calendarData: SearchItem[]
  contactsData: SearchItem[]
  dropboxData: SearchItem[]
  slackData: SearchItem[]
  tweetData: SearchItem[]
}

const SearchResults: FunctionComponent<Props> = (props) => {
  const {
    calendarData,
    contactsData,
    dropboxData,
    slackData,
    tweetData,
  } = props
  const { variable: filter } = useContext(useFilter)!
  const { variable: sortBy } = useContext(useSort)!
  const { variable: query } = useContext(useSearchQuery)!

  const renderItems = () => {
    let results = []

    if (filter.calendar) {
      results.push(
        calendarData
          .map((item) => item.data as CalendarData)
          .map((data) => <CalendarItem key={data.title} {...data} />)
      )
    }

    if (filter.contacts) {
      results.push(
        contactsData
          .map((item) => item.data as ContactsData)
          .map((data) => <ContactsItem key={data.name} {...data} />)
      )
    }

    if (filter.dropbox) {
      results.push(
        dropboxData
          .map((item) => item.data as DropboxData)
          .map((data) => <DropboxItem key={data.title} {...data} />)
      )
    }

    if (filter.slack) {
      results.push(
        slackData
          .map((item) => item.data as SlackData)
          .map((data) => <SlackItem key={data.message} {...data} />)
      )
    }

    if (filter.tweet) {
      results.push(
        tweetData
          .map((item) => item.data as TweetData)
          .map((data) => <TweetItem key={data.message} {...data} />)
      )
    }

    switch (sortBy) {
      case 'alphabetical':
        return alphabetical(results)
      case 'random':
        return shuffle(results)
      default:
        return flatten(results)
    }
  }

  const numItems = () => {
    return renderItems().length
  }

  const isEmpty = () => {
    return numItems() < 1
  }

  const renderNumResults = () => {
    let message

    if (query) {
      const plural = numItems() === 1 ? 'result' : 'results'
      message = isEmpty() ? 'No results' : `${numItems()} ${plural}`
    } else {
      message = `All results (${numItems()})`
    }

    return <p className='SearchResults-num'>{message}</p>
  }

  return (
    <>
      {renderNumResults()}
      <div className={isEmpty() ? 'SearchResults-emptyState' : 'SearchResults'}>
        {!isEmpty() && renderItems()}
      </div>
    </>
  )
}

export default SearchResults
